/**
 * API client.
 *
 * Two behaviours worth knowing about:
 *
 * 1. The access token lives in memory only, never in localStorage. A token in
 *    localStorage is readable by any script on the origin; in memory it dies
 *    with the tab, and the httpOnly refresh cookie is what survives a reload.
 *
 * 2. A 401 with `detail.expired` triggers ONE silent refresh and a replay of
 *    the original request. Concurrent 401s share a single in-flight refresh
 *    rather than each firing their own — the refresh endpoint rotates the
 *    token, so N parallel refreshes would rotate N times and the server would
 *    read the losers as token REUSE and revoke the whole session. A trader
 *    loading four panels at once would be signed out by their own dashboard.
 */

import type { ApiErrorBody } from '@apna/shared'

const BASE = '/api'

export class ApiRequestError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly requestId: string,
    readonly detail?: Record<string, unknown>,
  ) {
    super(message)
    this.name = 'ApiRequestError'
  }

  /** True when the access token expired and a refresh is worth attempting. */
  get isExpiredToken(): boolean {
    return this.status === 401 && this.detail?.expired === true
  }
}

let accessToken: string | null = null
/** Set by the auth store so a failed refresh can clear app state. */
let onAuthLost: (() => void) | null = null

export const setAccessToken = (token: string | null): void => {
  accessToken = token
}
export const getAccessToken = (): string | null => accessToken
export const setAuthLostHandler = (fn: (() => void) | null): void => {
  onAuthLost = fn
}

/** Shared in-flight refresh. See note 2 above. */
let refreshInFlight: Promise<boolean> | null = null

async function doRefresh(): Promise<boolean> {
  const res = await fetch(`${BASE}/auth/refresh`, {
    method: 'POST',
    credentials: 'include',
  })
  if (!res.ok) {
    accessToken = null
    onAuthLost?.()
    return false
  }
  const body = (await res.json()) as { accessToken: string }
  accessToken = body.accessToken
  return true
}

export function refreshSession(): Promise<boolean> {
  refreshInFlight ??= doRefresh().finally(() => {
    refreshInFlight = null
  })
  return refreshInFlight
}

interface RequestOptions extends Omit<RequestInit, 'body'> {
  body?: unknown
  /** Internal: prevents an infinite refresh loop. */
  _retried?: boolean
}

async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { body, _retried, headers, ...rest } = options

  const res = await fetch(`${BASE}${path}`, {
    ...rest,
    credentials: 'include',
    headers: {
      ...(body !== undefined ? { 'content-type': 'application/json' } : {}),
      ...(accessToken ? { authorization: `Bearer ${accessToken}` } : {}),
      ...headers,
    },
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
  })

  if (res.status === 204) return undefined as T

  if (!res.ok) {
    let parsed: ApiErrorBody | null = null
    try {
      parsed = (await res.json()) as ApiErrorBody
    } catch {
      // Non-JSON error (proxy, gateway). Fall through to a generic message.
    }

    const err = new ApiRequestError(
      res.status,
      parsed?.error.code ?? 'INTERNAL',
      parsed?.error.message ?? res.statusText,
      parsed?.error.requestId ?? '-',
      parsed?.error.detail,
    )

    // One silent refresh, then replay. Never on the refresh endpoint itself.
    if (err.isExpiredToken && !_retried && !path.startsWith('/auth/refresh')) {
      const ok = await refreshSession()
      if (ok) return request<T>(path, { ...options, _retried: true })
    }

    throw err
  }

  return (await res.json()) as T
}

export const api = {
  get: <T>(path: string, init?: RequestOptions) => request<T>(path, { ...init, method: 'GET' }),
  post: <T>(path: string, body?: unknown, init?: RequestOptions) =>
    request<T>(path, { ...init, method: 'POST', body }),
  patch: <T>(path: string, body?: unknown, init?: RequestOptions) =>
    request<T>(path, { ...init, method: 'PATCH', body }),
  put: <T>(path: string, body?: unknown, init?: RequestOptions) =>
    request<T>(path, { ...init, method: 'PUT', body }),
  del: <T>(path: string, init?: RequestOptions) => request<T>(path, { ...init, method: 'DELETE' }),
}

/** Build a query string, dropping undefined and empty values. */
export function qs(params: Record<string, string | number | boolean | undefined>): string {
  const out = new URLSearchParams()
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === '') continue
    out.set(k, String(v))
  }
  const s = out.toString()
  return s ? `?${s}` : ''
}
